import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Shield, Github, Twitter, Instagram, Mail } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { name: 'Features', href: '#features', isAnchor: true },
      { name: 'How AI Works', href: '/how-ai-works', isAnchor: false },
      { name: 'Download', href: '#download', isAnchor: true },
      { name: 'FAQ', href: '#faq', isAnchor: true },
    ],
    company: [
      { name: 'Charity', href: '/charity', isAnchor: false },
      { name: 'Contact', href: '/feedback', isAnchor: false },
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy', isAnchor: false },
      { name: 'Terms of Service', href: '/terms', isAnchor: false },
    ],
    support: [
      { name: 'Feedback', href: '/feedback', isAnchor: false },
      { name: 'Report a Bug', href: '/feedback?type=bug', isAnchor: false },
    ],
  };

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const trackFooterClick = (linkName: string, category: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'click', {
        event_category: `Footer - ${category}`,
        event_label: linkName,
        value: 1
      });
    }
  };

  const socialLinks = [
    { icon: Twitter, href: 'https://twitter.com/cyclara', label: 'Twitter' },
    { icon: Instagram, href: 'https://instagram.com/cyclara', label: 'Instagram' },
    { icon: Github, href: 'https://github.com/cyclara', label: 'GitHub' },
    { icon: Mail, href: 'mailto:hello@cyclara.app', label: 'Email' },
  ];

  return (
    <footer className="relative pt-24 pb-12 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyclara-50/50 to-cyclara-100/30" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyclara-500 to-lavender-500
                            flex items-center justify-center shadow-lg shadow-cyclara-500/30">
                <span className="text-white text-2xl">🌸</span>
              </div>
              <span className="text-2xl font-bold text-midnight-500">Cyclara</span>
            </Link>
            <p className="text-midnight-400 mb-6 leading-relaxed">
              Your private AI companion for menstrual wellness. Track your cycle, log your mood, 
              and get empathetic support - all 100% offline.
            </p>
            <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
              <Shield className="w-4 h-4" />
              <span>Your data never leaves your device</span>
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h4 className="font-semibold text-midnight-500 mb-4">Product</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  {link.isAnchor ? (
                    <a
                      href={link.href}
                      onClick={(e) => handleAnchorClick(e, link.href)}
                      className="text-midnight-400 hover:text-cyclara-500 transition-colors cursor-pointer"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      onClick={() => trackFooterClick(link.name, 'Product')}
                      className="text-midnight-400 hover:text-cyclara-500 transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-midnight-500 mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={() => trackFooterClick(link.name, 'Company')}
                    className="text-midnight-400 hover:text-cyclara-500 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-midnight-500 mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={() => trackFooterClick(link.name, 'Legal')}
                    className="text-midnight-400 hover:text-cyclara-500 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-midnight-500 mb-4">Support</h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={() => trackFooterClick(link.name, 'Support')}
                    className="text-midnight-400 hover:text-cyclara-500 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-cyclara-200 to-transparent mb-8" />

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Copyright */}
          <div className="flex items-center gap-2 text-midnight-400 text-sm">
            <span>© {currentYear} Cyclara. Made with</span>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
            >
              <Heart className="w-4 h-4 text-cyclara-500 fill-cyclara-500" />
            </motion.div>
            <span>for women everywhere</span>
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <motion.a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackFooterClick(social.label, 'Social')}
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="w-10 h-10 rounded-xl bg-white border border-cyclara-100
                         flex items-center justify-center text-midnight-400
                         hover:text-cyclara-500 hover:border-cyclara-300 hover:shadow-lg
                         transition-all duration-300"
                aria-label={social.label}
              >
                <social.icon className="w-5 h-5" />
              </motion.a>
            ))}
          </div>
        </div>

        {/* Privacy Reminder */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full 
                        bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
            <Shield className="w-5 h-5 text-green-600" />
            <span className="text-green-700 font-medium">
              100% Private • No Cloud • No Tracking • Your Data Stays Yours
            </span>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
